/**
 * ranking-estadual.js — apresentação do menu "Ranking Estadual" (Brasil / Truck) e o contexto
 * da diretoria do usuário logado.
 *
 * São os MESMOS rankings do Ranking Nacional (mesmas regras, desempate e tela —
 * router/views/rankings/nacional.vue com `meta.rankingEstadual`), só com a população recortada
 * pela diretoria da regional de quem está logado. Quem decide a diretoria é o backend
 * (salesteams_app_api, Model_rankings_estadual): o app nunca envia diretoria nem conhece os IDs
 * dos diretores — ele só pergunta "qual é a minha?" em `biteams/rk-estadual-contexto` para saber
 * se mostra o menu e qual nome exibir.
 *
 * `path` é a rota do app (e a permissão da secretária); `endpoint` é a URI da API — iguais de
 * propósito, ver config/routes.php do salesteams_app_api.
 *
 * O Ranking Estadual NÃO tem premiação (`semPremiacao: true` em todos): a tela esconde selo,
 * etiqueta, texto e destaque de "premiado". A API também devolve `premiados: 0` para eles.
 */
import Vue from "vue";
import { api } from "@/state/services";
import { INDICADORES_NACIONAL } from "@/utils/ranking-nacional";

// Chave geral do Ranking Estadual no app. Desligado: some do menu e do Regras e Regulamentos, a
// diretoria não é consultada na API e as rotas /biteams/rk-estadual-* voltam para o início.
// Para reativar, basta trocar para `true`.
export const RANKING_ESTADUAL_ATIVO = true;

export const GRUPOS_ESTADUAL = [
  { key: "brasil", label: "Brasil" },
  { key: "truck", label: "Truck" },
];

function ranking(cfg) {
  return {
    ...cfg,
    path: `/biteams/rk-${cfg.id}`,
    endpoint: `biteams/rk-${cfg.id}`,
    metricLabel: INDICADORES_NACIONAL[cfg.metrica].label,
    metricCurto: INDICADORES_NACIONAL[cfg.metrica].curto,
    fmt: INDICADORES_NACIONAL[cfg.metrica].fmt,
    entidadeLabel: cfg.entidade === "consultor" ? "Consultor" : "Regional",
    semPremiacao: true,
  };
}

/** Os 8 rankings, na ordem do menu. Brasil = TOP 20; Truck = TOP 05. */
export const RANKINGS_ESTADUAIS = [
  ranking({
    id: "estadual-brasil-regional-adesao",
    grupo: "brasil",
    menuLabel: "Top 20 Regionais Adesão",
    titulo: "TOP 20 Regionais — Adesão",
    subtitulo: "Ranking mensal das regionais da sua diretoria por adesões.",
    entidade: "regional",
    metrica: "adesoes",
  }),
  ranking({
    id: "estadual-brasil-regional-ticket",
    grupo: "brasil",
    menuLabel: "Top 20 Regionais Ticket Médio",
    titulo: "TOP 20 Regionais — Ticket Médio",
    subtitulo: "Ranking mensal das regionais da sua diretoria por ticket médio — participam as regionais com base ativa do mês anterior acima de 100.",
    entidade: "regional",
    metrica: "ticketMedio",
  }),
  ranking({
    id: "estadual-brasil-consultores-adesao",
    grupo: "brasil",
    menuLabel: "Top 20 Consultores Adesões",
    titulo: "TOP 20 Consultores — Adesões",
    subtitulo: "Ranking mensal dos consultores da sua diretoria (base menor que 300) por adesões.",
    entidade: "consultor",
    metrica: "adesoes",
  }),
  ranking({
    id: "estadual-brasil-consultores-boletos",
    grupo: "brasil",
    menuLabel: "Top 20 Consultores Boletos Quitados",
    titulo: "TOP 20 Consultores — Boletos Quitados",
    subtitulo: "Ranking mensal dos consultores da sua diretoria (base menor que 300) por boletos quitados.",
    entidade: "consultor",
    metrica: "boletosPagos",
  }),
  ranking({
    id: "estadual-truck-regional-adesao",
    grupo: "truck",
    menuLabel: "Top 05 Regionais Adesão",
    titulo: "TOP 05 Regionais Truck — Adesão",
    subtitulo: "Ranking mensal das regionais Truck da sua diretoria por adesões.",
    entidade: "regional",
    metrica: "adesoes",
  }),
  ranking({
    id: "estadual-truck-regional-boletos",
    grupo: "truck",
    menuLabel: "Top 05 Regionais Boletos Quitados",
    titulo: "TOP 05 Regionais Truck — Boletos Quitados",
    subtitulo: "Ranking mensal das regionais Truck da sua diretoria por boletos quitados.",
    entidade: "regional",
    metrica: "boletosPagos",
  }),
  ranking({
    id: "estadual-truck-consultor-adesao",
    grupo: "truck",
    menuLabel: "Top 05 Consultores Adesão",
    titulo: "TOP 05 Consultores Truck — Adesão",
    subtitulo: "Ranking mensal dos consultores Truck da sua diretoria (base menor que 300) por adesões.",
    entidade: "consultor",
    metrica: "adesoes",
  }),
  ranking({
    id: "estadual-truck-consultores-boletos",
    grupo: "truck",
    menuLabel: "Top 05 Consultores Boletos Quitados",
    titulo: "TOP 05 Consultores Truck — Boletos Quitados",
    subtitulo: "Ranking mensal dos consultores Truck da sua diretoria (base menor que 300) por boletos quitados.",
    entidade: "consultor",
    metrica: "boletosPagos",
  }),
];

export const RANKINGS_ESTADUAIS_POR_ID = RANKINGS_ESTADUAIS.reduce((acc, r) => {
  acc[r.id] = r;
  return acc;
}, {});

// ---------------------------------------------------------------------------
// Contexto: diretoria do usuário logado
// ---------------------------------------------------------------------------
// Estado reativo (Vue.observable) lido pelo menu (utils/menu-map.js) e pela tela. Fica guardado
// no localStorage amarrado ao token da sessão, para o menu não "piscar" a cada navegação nem a
// cada F5 — o layout main.vue é remontado a cada troca de rota. Novo login (outro token) ou
// logout invalidam (ver `limparContextoEstadual`, chamado no auth.module).

const CHAVE = "rk_estadual";

function marcaDoToken() {
  try {
    const token = window.localStorage.getItem("token") || "";
    return token ? token.slice(-24) : "";
  } catch (e) {
    return "";
  }
}

function lerSalvo() {
  try {
    const salvo = JSON.parse(window.localStorage.getItem(CHAVE) || "null");
    const marca = marcaDoToken();
    if (salvo && marca && salvo.t === marca) {
      return { carregado: true, diretoria: salvo.d || null };
    }
  } catch (e) {
    /* JSON inválido ou sem localStorage: carrega da API */
  }
  return { carregado: false, diretoria: null };
}

export const contextoEstadual = Vue.observable(lerSalvo());

let pendente = null;

/**
 * Busca a diretoria uma vez por sessão (no-op se já carregou). Em falha (rede, API antiga sem a
 * rota) o menu fica oculto só até o próximo login/recarga — sem refazer a chamada a cada tela.
 */
export function carregarContextoEstadual() {
  if (!RANKING_ESTADUAL_ATIVO) return Promise.resolve(null);
  if (contextoEstadual.carregado) return Promise.resolve(contextoEstadual.diretoria);
  if (pendente) return pendente;
  if (!marcaDoToken()) return Promise.resolve(null);

  const marcaPedido = marcaDoToken();
  const pedido = api
    .get("biteams/rk-estadual-contexto")
    .then((res) => {
      // Trocou de sessão no meio da chamada (logout/login): a resposta é de outra pessoa.
      if (marcaDoToken() !== marcaPedido) return null;
      const corpo = res.data || {};
      if (corpo.status !== "success") throw new Error("contexto indisponível");
      const d = corpo.diretoria && corpo.diretoria.key ? { key: String(corpo.diretoria.key), label: String(corpo.diretoria.label || "") } : null;
      contextoEstadual.diretoria = d;
      contextoEstadual.carregado = true;
      try {
        window.localStorage.setItem(CHAVE, JSON.stringify({ t: marcaDoToken(), d }));
      } catch (e) {
        /* sem localStorage: vale só para esta aba */
      }
      return d;
    })
    .catch(() => {
      if (marcaDoToken() !== marcaPedido) return null;
      contextoEstadual.diretoria = null;
      contextoEstadual.carregado = true; // só em memória: o próximo login/recarga tenta de novo
      return null;
    })
    .finally(() => {
      if (pendente === pedido) pendente = null;
    });
  pendente = pedido;
  return pendente;
}

/** Login novo / logout: esquece a diretoria da sessão anterior. */
export function limparContextoEstadual() {
  contextoEstadual.carregado = false;
  contextoEstadual.diretoria = null;
  pendente = null;
  try {
    window.localStorage.removeItem(CHAVE);
  } catch (e) {
    /* nada a limpar */
  }
}

/**
 * A resposta de um ranking estadual é a fonte mais recente: se ela trouxe a diretoria (ou disse
 * que não há nenhuma), o contexto do menu passa a refletir isso — sem esperar novo login.
 */
export function sincronizarContextoEstadual(diretoria) {
  const d = diretoria && diretoria.key ? { key: String(diretoria.key), label: String(diretoria.label || "") } : null;
  contextoEstadual.diretoria = d;
  contextoEstadual.carregado = true;
  try {
    window.localStorage.setItem(CHAVE, JSON.stringify({ t: marcaDoToken(), d }));
  } catch (e) {
    /* sem localStorage: vale só para esta aba */
  }
}

/** `true` quando o usuário logado pertence a uma diretoria do Ranking Estadual. */
export function temRankingEstadual() {
  return !!(RANKING_ESTADUAL_ATIVO && contextoEstadual.carregado && contextoEstadual.diretoria);
}
